'use client';

import { useState } from 'react';
import Image from 'next/image';

interface FeatureItem {
  title: string;
  description: string;
  learnMoreHref?: string;
  image?: string;
}

interface MobileFeatureAccordionProps {
  title: string;
  subtitle: string;
  items: FeatureItem[];
  featureImage: string;
}

export default function MobileFeatureAccordion({ title, subtitle, items, featureImage }: MobileFeatureAccordionProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-black text-xl font-medium leading-7 mb-2">
          {title}
        </h3>
        <p className="text-neutral-600 text-base font-normal leading-6 max-w-[320px] mx-auto">
          {subtitle}
        </p>
      </div>
      
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="bg-[#f4f9ff] rounded-[20px] overflow-hidden">
            <button
              onClick={() => toggleItem(index)}
              className="w-full p-5 flex items-center justify-between"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <h4 className="text-lg font-medium text-left">{item.title}</h4>
                {item.learnMoreHref && (
                  <a
                    href={item.learnMoreHref}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="text-[#0070f3] inline-flex items-center gap-1 text-xs font-medium hover:underline flex-shrink-0"
                  >
                    Learn more
                    <svg className="w-2.5 h-2.5" viewBox="0 0 12 12" fill="currentColor">
                      <path d="M6 0L12 6L6 12" />
                    </svg>
                  </a>
                )}
              </div>
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0 ml-3">
                <svg
                  className={`w-3 h-3 transition-transform ${expandedIndex === index ? 'rotate-90' : ''}`}
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.5 2L8.5 6L4.5 10"
                    stroke="#0070f3"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
            
            {expandedIndex === index && (
              <div className="px-5 pb-5 pt-0 space-y-4">
                <p className="text-sm text-[#505050] leading-relaxed">
                  {item.description}
                </p>
                <div className="relative w-full h-[335px] rounded-[12px] overflow-hidden">
                  <Image 
                    src={item.image || featureImage} 
                    alt={item.title}
                    fill
                    className="object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

